import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

/**
 * Hook for fetching product recommendations with filtering and caching.
 *
 * @param {string|null} productId - Product ID or SKU to fetch recommendations for
 * @param {Object} filters - Filter options
 * @param {string[]} filters.relationTypes - Array of relation types to include
 * @param {number} filters.minScore - Minimum score threshold
 * @param {number} filters.limit - Maximum number of results per type
 * @param {string} filters.direction - 'out', 'in', or 'both'
 */
export default function useRecommendations(productId, filters = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    relationTypes = ['complement', 'addon', 'competitor'],
    minScore = 0,
    limit = 100,
    direction = 'out',
  } = filters;

  const fetchRecommendations = useCallback(async () => {
    if (!productId) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('anchor', productId);
      params.set('relation_types', relationTypes.join(','));
      params.set('min_score', String(minScore));
      params.set('limit', String(limit));
      params.set('direction', direction);

      const { data: response } = await axios.get(`${API_BASE}/api/relations/graph?${params.toString()}`);

      // Group recommendations by relation type
      const grouped = {
        complement: [],
        addon: [],
        competitor: [],
      };

      // Find the anchor product node
      const anchorNode = (response.nodes || []).find(n => n.is_anchor);

      // Process edges and group by relation type
      (response.edges || []).forEach(edge => {
        const relationType = edge.relation_type || 'complement';
        if (!grouped[relationType]) {
          grouped[relationType] = [];
        }

        // Find the candidate node
        const candidateId = edge.from === anchorNode?.id ? edge.to : edge.from;
        const candidateNode = (response.nodes || []).find(n => n.id === candidateId);

        if (candidateNode) {
          const scoreValue = edge.match_score || edge.score || 0;
          grouped[relationType].push({
            ...candidateNode,
            // Map node id to target_product_id for card compatibility
            target_product_id: candidateNode.id || candidateNode.product_id,
            // Include source/anchor product ID for overrides
            source_product_id: anchorNode?.id,
            // Map product fields
            product_name: candidateNode.label || candidateNode.product_name || candidateNode.title,
            image_url: candidateNode.image || candidateNode.image_url,
            price: candidateNode.price,
            // Relation data
            relation_type: relationType,
            score: scoreValue, // Main score field used by cards
            match_score: scoreValue,
            upsell_score: edge.upsell_score || 0,
            competitor_score: edge.competitor_score || 0,
            co_purchase_score: edge.co_purchase_score || 0,
            combined_sale_likelihood: edge.combined_sale_likelihood || 0,
            source: edge.source || 'rule-v1',
            tags: edge.tags || [],
            override: edge.override || null,
            edge_id: edge.id,
          });
        }
      });

      // Sort each group by score descending
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => (b.match_score || 0) - (a.match_score || 0));
      });


      setData({
        product: anchorNode || null,
        recommendations: grouped,
        totals: {
          complement: grouped.complement.length,
          addon: grouped.addon.length,
          competitor: grouped.competitor.length,
        },
        raw: response,
      });
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
      setError(err.message || 'Failed to fetch recommendations');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productId, relationTypes, minScore, limit, direction]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Flatten grouped recommendations into a single array
  const recommendations = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.recommendations?.complement || []),
      ...(data.recommendations?.addon || []),
      ...(data.recommendations?.competitor || []),
    ];
  }, [data]);

  /**
   * Optimistically update an override for a specific recommendation.
   * This updates the local state immediately without refetching.
   */
  const updateOverride = useCallback((targetProductId, relationType, override) => {
    setData(prev => {
      if (!prev?.recommendations) return prev;

      const newRecommendations = { ...prev.recommendations };

      // Update the item in the appropriate group
      if (newRecommendations[relationType]) {
        newRecommendations[relationType] = newRecommendations[relationType].map(item => {
          if (item.target_product_id === targetProductId) {
            return { ...item, override };
          }
          return item;
        });
      }

      return { ...prev, recommendations: newRecommendations };
    });
  }, []);

  return {
    data,
    recommendations,
    groupedRecommendations: data?.recommendations || { complement: [], addon: [], competitor: [] },
    loading,
    error,
    refetch: fetchRecommendations,
    updateOverride,
  };
}
